import {
  BatchUpdateStepNoticesModel,
  CreateStepContentModel,
  CreateStepModel,
  CreateStepNoticeModel,
  List,
  StepContentModel,
  StepModel,
  StepNoticeModel,
  StepNoticeWithContentModel,
  StepWithContentModel,
  UpdateStepContentModel,
  UpdateStepNoticeModel,
} from '@partful/instructions-domain-models';
import { BaseService } from '@partful/shared/api/baseService';

export class StepsService extends BaseService {
  async create(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    args: CreateStepModel
  ): Promise<StepModel> {
    const res = await this.client.post(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps`,
      args,
      { params: { account } }
    );
    return res.data;
  }

  async list(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    args: { nextToken?: string; lang?: string }
  ): Promise<List<StepModel & Partial<StepWithContentModel>>> {
    const res = await this.client.get<List<StepModel | StepWithContentModel>>(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps`,
      { params: { account, ...args } }
    );
    return res.data;
  }

  async delete(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string
  ): Promise<void> {
    await this.client.delete(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}`,
      {
        params: { account },
      }
    );
  }

  async createContent(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    args: CreateStepContentModel
  ): Promise<StepContentModel> {
    const response = await this.client.post(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/contents`,
      args,
      { params: { account } }
    );
    return response.data;
  }

  async updateContent(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    lang: string,
    updateArgs: UpdateStepContentModel
  ): Promise<StepContentModel> {
    const res = await this.client.patch(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/contents`,
      updateArgs,
      { params: { account, lang } }
    );
    return res.data;
  }

  async createNotice(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    args: CreateStepNoticeModel
  ): Promise<StepNoticeModel & Partial<StepNoticeWithContentModel>> {
    const response = await this.client.post(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/notices`,
      args,
      { params: { account } }
    );
    return response.data;
  }

  async listNotices(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    args: { lang: string; nextToken?: string }
  ): Promise<List<StepNoticeModel & Partial<StepNoticeWithContentModel>>> {
    const response = await this.client.get(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/notices`,
      { params: { account, ...args } }
    );
    return response.data;
  }

  async updateNotice(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    stepNoticeId: string,
    lang: string,
    args: UpdateStepNoticeModel
  ): Promise<(StepNoticeModel & Partial<StepNoticeWithContentModel>) | null> {
    const response = await this.client.patch(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/notices/${stepNoticeId}`,
      args,
      { params: { account, lang } }
    );
    return response.data;
  }

  async batchUpdateNotices(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    args: BatchUpdateStepNoticesModel
  ) {
    const response = await this.client.patch(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/notices`,
      args,
      { params: { account } }
    );
    return response.data;
  }

  async deleteNotice(
    account: string,
    instructionId: string,
    versionId: string,
    segmentId: string,
    stepId: string,
    stepNoticeId: string
  ): Promise<void> {
    await this.client.delete(
      `/instructions/${instructionId}/versions/${versionId}/segments/${segmentId}/steps/${stepId}/notices/${stepNoticeId}`,
      { params: { account } }
    );
  }
}
